


















































































































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BpcInputGroup from '@shared/components/forms/inputs/input-group/input-group.vue';
import BpcTextarea from '@shared/components/forms/inputs/textarea/textarea.vue';
import BpcSelect from '@shared/components/forms/inputs/select/select.vue';
import BpcSwitch from '@shared/components/forms/inputs/switch/switch.vue';
import { POSDiscountFormModel } from '@pos/interfaces/pos-v2';
import { CenterSetting } from '@core/interfaces/center-setting';

@Component({
  name: 'bpc-pos-price-edition-modal',
  components: {
    BpcInputGroup,
    BpcSelect,
    BpcTextarea,
    BpcSwitch,
  },
})
export default class PosPriceEditionModal extends Vue {
  // PROPS
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop({
    type: Number,
    required: true,
  })
  readonly posSessionId!: number;
  @Prop({
    type: Number,
    required: true,
  })
  readonly productId!: number;
  @Prop({
    type: Number,
    required: true,
  })
  readonly ticketId!: number;

  @Prop({
    type: Number,
  })
  readonly productQuantity: number;
  @Prop({
    type: Number,
  })
  readonly productPrice: number;
  @Prop({
    type: Boolean,
    default: false,
  })
  readonly freeEditPriceAllowed: boolean;
  @Prop({
    type: Boolean,
    default: false,
  })
  readonly editableQuantity: boolean;
  @Prop({
    type: Boolean,
    default: false,
  })
  readonly allowNegativeQuantity: boolean;
  @Prop({
    type: String,
  })
  readonly description: string;
  @Prop({
    type: String,
    required: true,
  })
  readonly discountType: 'percentage' | 'fixed';
  @Prop({
    type: Number,
    required: true,
  })
  readonly discount: number;
  @Prop({
    type: Boolean,
    required: true,
  })
  readonly discountEditedPrice: boolean;

  // DATA
  saving = false;

  formModel: POSDiscountFormModel = {
    discountAmount: 0,
    discountType: 'percentage',
    discountPercentage: 0,
    unitPrice: 0,
    remarks: '',
    applyToAllLines: false,
    productId: null,
    quantity: 1,
  };

  getDiscountType() {
    if (this.discountType === 'percentage' && this.discount && this.discount > 0) {
      return 'percentage';
    }
    if (this.discountType === 'fixed') {
      return 'fixed';
    }
    if (this.discountEditedPrice) {
      return 'free_unit_price';
    }
    return 'no_discount';
  }

  // COMPUTED
  // LIFECYCLE
  created() {
    this.formModel.unitPrice = this.productPrice;
    this.formModel.quantity = this.productQuantity;
    this.formModel.remarks = this.description;
    this.formModel.discountType = this.getDiscountType();
    this.formModel.discountAmount = this.discountType === 'fixed' ? this.discount : 0;
    this.formModel.discountPercentage = this.discountType === 'percentage' ? this.discount : 0;
    //this.resetUnitPrice();
  }

  // METHODS

  closeModal() {
    this.restartValues();
    this.$emit('close');
  }

  get unitPrice() {
    return this.productPrice ? this.productPrice : 0;
  }

  get quantityDisabled(): boolean {
    return (
      this.saving ||
      !this.editableQuantity ||
      (!this.allowNegativeQuantity && this.productQuantity < 0)
    );
  }

  restartValues() {
    this.formModel = {
      discountAmount: 0,
      discountType: 'percentage',
      discountPercentage: 0,
      unitPrice: 0,
      remarks: '',
      applyToAllLines: false,
      productId: null,
      quantity: 1,
    };
  }

  get totalAmount() {
    let totalAmount = this.formModel.unitPrice * this.formModel.quantity;
    totalAmount = totalAmount - (totalAmount * this.formModel.discountPercentage) / 100;
    totalAmount = totalAmount - this.formModel.discountAmount;
    return totalAmount;
  }

  get centerSetting(): CenterSetting {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  get discountAllowed(): boolean {
    return !!this.centerSetting.pos_discounts_allowed || this.$authService.isAdmin();
  }

  getDiscountTypeOptions() {
    const arrayOption = [];
    arrayOption.push({ value: 'no_discount', text: this.$t('pos.no_discount') });
    if (this.discountAllowed) {
      arrayOption.push({ value: 'percentage', text: this.$t('pos.discount_type_percentage') });
      arrayOption.push({ value: 'fixed', text: this.$t('pos.discount_type_fixed') });
    }
    return [...arrayOption];
  }

  getAllowedQuantity() {
    if (this.allowNegativeQuantity) {
      return { type: 'number', step: '1' };
    }
    return { type: 'number', min: '1', step: '1' };
  }

  async onConfirm() {
    this.saving = true;
    try {
      if (!this.checkValidFormModel()) {
        this.saving = false;
        return;
      }
      const formModel = {
        ...this.formModel,
        productId: this.productId,
        discountAmount:
          this.formModel.discountType === 'fixed' ? this.formModel.discountAmount : null,
        discountPercentage:
          this.formModel.discountType === 'percentage' ? this.formModel.discountPercentage : null,
        freeUnitPrice: this.freeEditPriceAllowed ? this.formModel.unitPrice : null,
      };
      const ticket = await this.$posService.editPrice(this.posSessionId, this.ticketId, formModel);
      this.$emit('ticket-detail-updated', ticket);
      this.closeModal();
      this.saving = false;
      this.restartValues();
    } catch (error) {
      this.handleError(error);
      this.saving = false;
    }
  }

  checkValidFormModel(): boolean {
    if (!this.allowNegativeQuantity && this.formModel.quantity < 1) {
      // NOTE: in special cases it is needed to allow negative quantities even
      // if the product do not allow it e.g. refund tickets. So, we only display
      // the error of negative quantity in the case that the user has manually
      // edited the quantity in the form.
      const quantityHasChanged = this.formModel.quantity !== this.productQuantity;
      if (quantityHasChanged) {
        this.$bpcMessage.showErrorMessage({
          text: 'pos.modal.error.quantity_must_be_positive',
        });
        return false;
      }
    }
    if (this.formModel.quantity == 0) {
      this.$bpcMessage.showErrorMessage({
        text: 'pos.modal.error.quantity_can_not_be_null',
      });
      return false;
    }
    if (this.formModel.discountType === 'percentage' && this.formModel.discountPercentage < 0) {
      this.$bpcMessage.showErrorMessage({
        text: 'pos.modal.error.percentage_must_be_positive',
      });
      return false;
    }
    return true;
  }

  async resetUnitPrice() {
    this.saving = true;
    if (this.freeEditPriceAllowed) {
      const originalPrice = await this.$posService.getOriginalPrice(
        this.posSessionId,
        this.ticketId,
        this.productId,
      );
      this.formModel.unitPrice = originalPrice;
      this.formModel.discountAmount = 0;
      this.formModel.discountPercentage = 0;
    }
    this.saving = false;
  }

  handleError(error: any) {
    console.log(error);
  }
  // WATCHERS
  @Watch('showModal')
  onShowModalChange(value: boolean) {
    if (value) {
      this.formModel.unitPrice = this.productPrice;
      this.formModel.quantity = this.productQuantity;
      this.formModel.remarks = this.description;
      this.formModel.discountType = this.getDiscountType();
      this.formModel.discountAmount = this.discountType === 'fixed' ? this.discount : 0;
      this.formModel.discountPercentage = this.discountType === 'percentage' ? this.discount : 0;
      //this.resetUnitPrice();
    }
  }
  @Watch('formModel.discountType')
  onDiscountTypeChange(newVal: string) {
    if (newVal === 'no_discount') {
      this.resetUnitPrice();
    }
    if (newVal === 'percentage') {
      this.formModel.discountAmount = 0;
    }
    if (newVal === 'fixed') {
      this.formModel.discountPercentage = 0;
    }
  }
}
